// Selectors
import { ownProfileEntity } from 'selectors'

// Types
import { UserSong, State, LicenseRevenue, RevenueReportingStatus } from 'types'

export const selectUserSongWithSongs = (state: State, userSongId: number): UserSong | null => {
  const { songs, userSongs } = state.entities
  const userSong = userSongs[String(userSongId)]
  if (!userSong) {
    return null
  }
  const samplesWithSongs = userSong?.samples.map(sample => ({
    ...sample,
    song: songs[String(sample.song)],
  }))
  return {
    ...userSong,
    samples: samplesWithSongs,
  }
}

export const selectUserSongListWithSongs = (state: State, userSongList: Array<UserSong>): Array<UserSong | null> =>
  userSongList.map(userSong => selectUserSongWithSongs(state, userSong.id))

type StepCompleted = {
  songInformationCompleted: boolean
  rightsholdersCompleted: boolean
  recordingRegistrationCompleted: boolean
  worksRegistrationCompleted: boolean
  releaseSongCompleted: boolean
}

export const selectUserSongStepsCompleted = (userSong: UserSong): StepCompleted => {
  const isLocked = userSong.state === 'ACTIVE' || userSong.state === 'INVALID' || userSong.state === 'TERMINATED'
  return {
    songInformationCompleted:
      (!!userSong.artist_name && !!userSong.song_title && !!userSong.runtime && !!userSong.audio_file_name) || isLocked,
    rightsholdersCompleted: userSong.rightsholders.reduce((agg, { share }) => agg + share, 0).toFixed(1) === '100.0' || isLocked,
    recordingRegistrationCompleted: userSong.recording_registered || isLocked,
    worksRegistrationCompleted: userSong.work_registered || isLocked,
    releaseSongCompleted: userSong.song_released || isLocked,
  }
}

export type UserSongStep = {
  id: number
  name: string
  cta: string
  description: string
  completed: boolean
  modal: string
}

export const selectUserSongSteps = (userSong: UserSong): Array<UserSongStep> => {
  const {
    songInformationCompleted,
    recordingRegistrationCompleted,
    worksRegistrationCompleted,
    releaseSongCompleted,
    rightsholdersCompleted,
  } = selectUserSongStepsCompleted(userSong)
  return [
    {
      id: 1,
      name: 'Song information',
      cta: 'Enter information',
      description: songInformationCompleted
        ? 'Information about your song, including final title and performers.'
        : 'Please provide some more information about your new song, including final title and performers.',
      completed: songInformationCompleted,
      modal: 'songinfo',
    },
    {
      id: 2,
      name: 'Rightsholders',
      cta: 'Add rightsholders',
      description: rightsholdersCompleted
        ? 'Full details of all rightsholders.'
        : 'Please provide details of all rightsholders on your new song.',
      completed: rightsholdersCompleted,
      modal: 'rightsholders',
    },
    {
      id: 3,
      name: 'Works registration',
      cta: 'Register work',
      description: worksRegistrationCompleted
        ? 'How you registered your composition.'
        : 'You need to register your work/composition at a PRO. If you haven’t done this before, don’t worry - we will guide you.',
      completed: worksRegistrationCompleted,
      modal: 'worksregistration',
    },
    {
      id: 4,
      name: 'Recording registration',
      cta: 'Register recording',
      description: recordingRegistrationCompleted
        ? 'Confirmation of recording registration.'
        : 'Please confirm your recording registration.',
      completed: recordingRegistrationCompleted,
      modal: 'registerrecording',
    },
    {
      id: 5,
      name: 'Distribution',
      cta: 'Confirm release',
      description: releaseSongCompleted
        ? 'The distributor of your new song and its ISRC.'
        : 'Confirm how your song is distributed and its ISRC.',
      completed: releaseSongCompleted,
      modal: 'releasesong',
    },
  ]
}

export const selectUserSongLicenseInfo = (state: State, userSongId: number): LicenseRevenue | null | undefined => {
  const userSong = state.entities.userSongs[String(userSongId)]
  if (!userSong) {
    return null
  }
  const sample = userSong.samples[0]
  const licensePriceCategory = state.entities.songs[String(sample.song)].licenseinfo.license_price_category
  const { licenseRevenues } = state.entities
  const licenseRevenuesList = Object.keys(licenseRevenues).map(id => licenseRevenues[id])
  return licenseRevenuesList.find(
    licenseRevenue => licenseRevenue.category === licensePriceCategory && sample.license_sample_type === licenseRevenue.sample_type,
  )
}

export const selectCurrencyConverter =
  (state: State) =>
  (
    values: Array<{ amount: number; currency: string }>,
  ): {
    amount: number
    currency: string
  } => {
    const { currencies } = state.entities
    if (!Object.keys(currencies).length) {
      return { amount: 0, currency: '' }
    }
    const profile = ownProfileEntity(state)
    const { rate: masterRate } = currencies[profile.currency]
    return {
      amount: values.reduce((agg, { currency, amount }) => {
        if (!currency) {
          return agg
        }
        const { rate } = currencies[currency]
        const exchangeRate = rate / masterRate
        return agg + (+amount || 0) / exchangeRate
      }, 0),
      currency: profile.currency,
    }
  }

export const selectRevenueReportingStatus = (state: State): RevenueReportingStatus => {
  const { revenueOrders, userSongRevenueReports } = state.entities
  const revenueOrderList = Object.keys(revenueOrders).map(id => revenueOrders[id])
  const userSongRevenueReportsList = Object.keys(userSongRevenueReports).map(id => userSongRevenueReports[id])
  const nowDate = new Date()
  nowDate.setHours(0, 0, 0, 0)
  const openRevenueReports =
    userSongRevenueReportsList.length > 0 &&
    userSongRevenueReportsList.filter(
      ({ paid, period }) => !paid && new Date(period.reporting_open) <= nowDate && new Date(period.reporting_close) >= nowDate,
    )
  const notReportedRevenueReports = userSongRevenueReportsList.filter(
    ({ reported, period }) => !reported && new Date(period.reporting_open) <= nowDate,
  )
  const latestRevenueOrder = revenueOrderList.sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime())[0]
  const reportingOpen = openRevenueReports && openRevenueReports.length > 0
  return {
    reportingOpen,
    reportingNeeded: openRevenueReports && openRevenueReports.some(report => !report.reported),
    paymentNeeded: latestRevenueOrder && !!latestRevenueOrder.due_date && notReportedRevenueReports?.length === 0,
    dueDate: (openRevenueReports && openRevenueReports.length > 0 && openRevenueReports[0].period.reporting_close) || '',
  }
}
